// src/pages/Admin/Overview.js
import React, { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import styles from './Overview.module.css';

export default function Overview() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const data = await adminApi.getOverviewStats();
            setStats(data);
        } catch (err) {
            setError(err.message || 'Failed to load stats');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading stats...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    if (!stats) return null;

    return (
        <div className={styles.overview}>
            <h1 className={styles.pageTitle}>Admin Overview</h1>

            {/* Users Section */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Users</h2>
                <div className={styles.statsGrid}>
                    <StatCard label="Total Users" value={stats.users.total} />
                    <StatCard label="New Today" value={stats.users.newToday} trend="up" />
                    <StatCard label="New This Week" value={stats.users.newThisWeek} />
                </div>
            </section>

            {/* Quizzes Section */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Quizzes</h2>
                <div className={styles.statsGrid}>
                    <StatCard label="Total Quizzes" value={stats.quizzes.total} />
                    <StatCard label="Today" value={stats.quizzes.today} />
                    <StatCard label="Passed" value={stats.quizzes.passed} variant="success" />
                    <StatCard label="Failed" value={stats.quizzes.failed} variant="warning" />
                    <StatCard label="Pass Rate" value={`${stats.quizzes.passRate}%`} />
                    <StatCard label="Avg Score" value={stats.quizzes.avgScore} />
                    <StatCard label="Legendary Unlocks" value={stats.quizzes.legendaryUnlocks} variant="legendary" />
                </div>
            </section>

            {/* Coins Section */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Coins</h2>
                <div className={styles.statsGrid}>
                    <StatCard label="Total Coins" value={stats.coins.total} />
                    <StatCard label="Today" value={stats.coins.today} trend="up" />
                    <StatCard label="Bronze" value={stats.coins.bronze} variant="bronze" />
                    <StatCard label="Silver" value={stats.coins.silver} variant="silver" />
                    <StatCard label="Gold" value={stats.coins.gold} variant="gold" />
                    <StatCard label="Legendary" value={stats.coins.legendary} variant="legendary" />
                    <StatCard label="Mastery" value={stats.coins.mastery} variant="mastery" />
                    <StatCard label="Collection Rewards" value={stats.coins.collectionReward} />
                </div>
            </section>

            {/* Collections Section */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Collections</h2>
                <div className={styles.statsGrid}>
                    <StatCard label="Active Collections" value={stats.collections.total} />
                    <StatCard label="Completed" value={stats.collections.completed} variant="success" />
                    <StatCard label="Completed Today" value={stats.collections.completedToday} />
                </div>
            </section>

            {/* Feedback & Reports */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Feedback & Reports</h2>
                <div className={styles.statsGrid}>
                    <StatCard label="New Feedback" value={stats.feedback.new} variant="alert" />
                    <StatCard label="Unresolved Feedback" value={stats.feedback.unresolved} />
                    <StatCard label="New Quiz Reports" value={stats.quizReports.new} variant="alert" />
                    <StatCard label="Unresolved Quiz Reports" value={stats.quizReports.unresolved} />
                </div>
            </section>

            {/* Quiz Quality */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Quiz Quality</h2>
                <div className={styles.statsGrid}>
                    <StatCard label="Total Answers" value={stats.quizQuality.totalAnswers} />
                    <StatCard label="Avg Correct Rate" value={`${stats.quizQuality.avgCorrectRate}%`} />
                </div>

                {stats.quizQuality.mostReportedQuestions?.length > 0 && (
                    <div className={styles.reportedQuestions}>
                        <h3 className={styles.subTitle}>Most Reported Questions</h3>
                        {stats.quizQuality.mostReportedQuestions.map((q, i) => (
                            <div key={i} className={styles.reportItem}>
                                <div className={styles.reportTitle}>{q.title}</div>
                                <div className={styles.reportQuestion}>{q.question_text}</div>
                                <div className={styles.reportCount}>{q.report_count} reports</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function StatCard({ label, value, variant, trend }) {
    const variantClass = variant ? styles[`stat${variant.charAt(0).toUpperCase() + variant.slice(1)}`] : '';
    const trendClass = trend ? styles[`trend${trend.charAt(0).toUpperCase() + trend.slice(1)}`] : '';

    return (
        <div className={`${styles.statCard} ${variantClass} ${trendClass}`}>
            <div className={styles.statLabel}>{label}</div>
            <div className={styles.statValue}>{value}</div>
        </div>
    );
}